import { useApolloClient, useMutation } from '@apollo/react-hooks'
import MaxAlert from '@berlitz/alert'
import InfoCircle from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/14-Alerts/InformationCircle'
import ArrowDown from '@berlitz/streamline-icons/lib/bold/52-Arrows-Diagrams/01-Arrows/ArrowDown1'
import ArrowUp from '@berlitz/streamline-icons/lib/bold/52-Arrows-Diagrams/01-Arrows/ArrowUp1'
import Calendar1 from '@berlitz/streamline-icons/lib/regular/01-Interface-Essential/21-Date-Calendar/Calendar1'
import Warning from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/14-Alerts/AlertTriangle'
import BookedLessonCard from '@berlitzplatforms/micro.ui.booked-lesson-card'
import BookingInformationModal from '@berlitzplatforms/micro.modal.booking-information'
import { DateRanges, StringRange } from '@berlitzplatforms/micro.ui.scheduling-calendar/lib'
import PortalProgram from '@classes/Program'
import { LoadingModal, WarningModal } from '@components/Modal'
import Timer from '@berlitzplatforms/micro.ui.timer'
import Tooltip from '@components/Tooltip'
import useRefetchProfile from '@hooks/useRefetchProfile'
import { InstructorHours } from '@interfaces/InstructorHours'
import { CreateTempHoldResponse } from '@interfaces/TempHold'
import { UserProfileProps } from '@interfaces/UserProfile'
import Spacer from '@berlitz/spacer'
import { BOOK_SESSIONS, CONFIRM_RESCHEDULE } from '@mutations/sessions'
import { CREATE_TEMP_HOLD, DELETE_RECORD } from '@mutations/tempHold'
import { isGodMode } from '@utils/helpers'
import moment from 'moment-timezone'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Skeleton from 'react-loading-skeleton'
import {
  AlertWrapper,
  DContent,
  DWrapper,
  ExpandBtn,
  Footer,
  Header,
  InfoWrapper,
  LessonSelected,
  LessonSelectedCount,
  LessonWrapper,
  MWrapper,
  ShadowExpand,
  StyledBtn,
  Submit,
  Title,
  TimeWrapper,
  NoLessonsSelected,
  InstructorSessions,
  BookedLessonCardWrapper,
  BulkBookButton,
} from './style'
import Modal from '@berlitzplatforms/micro.ui.modal'
import TransactionalModal from '@berlitzplatforms/micro.modal.transactional'
import ImageCalendarEventUnavailable from '@berlitzplatforms/micro.image.calendar-event-unavailable'
import { flatten, isEmpty, orderBy } from 'lodash'
import { DATE_FORMAT, PORTAL_ORIGIN } from '@utils/constants'
import useTempHoldSessions from '@hooks/useTempHoldSeshStorage'
import { IInstructorLessons } from '../interface'
import { InstructorName } from './style'
import { CrossInstrcutorContext, FMIStudentProfileContext } from '../../context'
import { getBulkSidebarLessons, updateDHRemaining } from './helpers'
import { usePortalFeatures } from '@components/RoleBasedView'
import useTimeFormat from '@hooks/useTimeFormat'
import Button from '@berlitz/button'
import SessionPreferenceModal from '@components/Modal/SessionPreferenceModal'
import { PUT_PROGRAMS } from '@mutations/programs'
import { FetchResult } from '@apollo/client'
import { useSelector } from 'react-redux'
import { StoreProps } from '@interfaces/StoreState'
export interface SidebarProps {
  loading?: boolean
  current?: StringRange
  failed: DateRanges
  selected: DateRanges
  instructorLessons?: IInstructorLessons
  deselected?: StringRange
  profile: UserProfileProps
  program: PortalProgram
  instructor: string
  lessons: BookedLesson[]
  disabled?: boolean
  disableBookButton?: boolean
  deleteAllSilently?: boolean
  instructorHours?: InstructorHours
  setters: {
    failed: (data: DateRanges) => void
    loading: (data: DateRanges) => void
    maxSelect: (data: number) => void
    lessons: (data: BookedLesson[]) => void
    selectLesson: (data: BookedLesson, status: TBookedLessonStatus) => void
    deselectLesson: (data?: BookedLesson, status?: TDeleteLessonStatus) => void
    deselectAllLesson: () => void
    current: (data: StringRange | undefined) => void
    deselected: (data: StringRange) => void
    disabled: (data: boolean) => void
    deleteAllSilently: (data: boolean) => void
    selected: (data: DateRanges) => void
    instructorHours: (data?: InstructorHours) => void
    notSaved: (data: boolean) => void
    deleteStorage: (data: boolean) => void
  }
  refresh: () => Promise<void>
  readOnly: boolean
}

export interface BookedLesson {
  Id?: string
  StudentId: string
  InstructorId: string
  ProgramId: string
  StartTime: string
  EndTime: string
  Status: TBookedLessonStatus
  disabled?: boolean
  isExcess?: boolean
}

export type TBookedLessonStatus = 'loading' | 'error' | 'created'
export type TDeleteLessonStatus = 'loading' | 'disabled' | 'deleted'

export const Sidebar: React.FC<SidebarProps> = ({
  loading,
  current,
  profile,
  program,
  instructor,
  failed,
  lessons,
  deselected,
  selected,
  instructorLessons,
  disabled,
  disableBookButton,
  deleteAllSilently,
  setters,
  instructorHours,
  refresh,
  readOnly,
}) => {
  const { isTimeFormatDisabled, convertToUserTimeFormats } = useTimeFormat()
  const {
    baseRoute,
    getRouteParams,
    isAdmin: isRegionalUser,
    isSessionAdmin: isAdmin,
    adminCustomerId: UserId,
    isBulkPage,
  } = useContext(FMIStudentProfileContext)
  const godMode = isGodMode(isAdmin) || __DEV__
  const intl = useIntl()
  const router = useRouter()
  const client = useApolloClient()
  const refetchProfile = useRefetchProfile()
  const { state } = useContext(CrossInstrcutorContext)
  const { state: FMIBulkSearchState } = usePortalFeatures({
    configId: 'flag::FMI-bulksearch',
  })
  const { deletingAllTempSession } = useTempHoldSessions(program.ProgramId || '')
  const sessionIdQuery = router.query.sessionId
  const isForBulkUsersOnly = FMIBulkSearchState.allowed && program?.hasBulkOnlyFeature()

  const { userPermissions } = useSelector(({ permissions }: StoreProps) => ({
    userPermissions: permissions.permissions,
  }))
  const SessionPrefFlag = userPermissions['flag::SessionPreference-feature']?.show

  const [expand, setExpand] = useState(false)
  const [lessonCount, setLessonCount] = useState(0)
  const [openWarningModal, setOpenWarningModal] = useState(false)
  const [openUnknownError, setOpenUnknownError] = useState(false)
  const [openPrefModal, setOpenPrefModal] = useState(false)
  const [validationFailed, setValidationFailed] = useState(false)
  const [loadingModal, setLoadingModal] = useState(false)
  const [bookingInfoModal, showBookingInfoModal] = useState(false)
  const [sessionExpiredModal, setSessionExpiredModal] = useState(false)
  const [confirmedLessons, setConfirmedLessons] = useState<string[]>([])
  const [unavailableLessons, setUnavailableLessons] = useState<string[]>([])
  const [showGroupToolTip, setShowGroupToolTip] = useState(true)
  const [putTempHold, { data: tempHoldData, loading: tempHoldLoading, error: tempHoldError }] =
    useMutation<CreateTempHoldResponse>(CREATE_TEMP_HOLD, { onError: () => {} }) // add onerror so the error wont propagate to error boundary
  const [lessonsRemaining, setLessonsRemaining] = useState(0)

  const [deleteRecord, { data: deleteData, error: deleteError }] = useMutation(DELETE_RECORD, {
    onError: () => {},
  }) // add onerror so the error wont propagate to error boundary

  const [deleteAllRecord, { data: deleteAllData, loading: deletingAll, error: deleteAllError }] = useMutation(
    DELETE_RECORD,
    {
      onCompleted: () => state.allowed && setters.deselectAllLesson(),
      onError: () => {},
    }
  ) // add onerror so the error wont propagate to error boundary

  const deleteAll = (otherLessons?: string) => {
    setters.disabled(true)
    setOpenWarningModal(false)
    deleteAllRecord({
      variables: {
        ids:
          otherLessons ||
          lessons
            .map((lesson) => {
              lesson.disabled = true
              return lesson.Id
            })
            .filter(Boolean)
            .join(','),
      },
    })
    setters.loading(selected)
  }

  const turnOffLoading = () => {
    setters.loading([])
    if (program.program) {
      setters.maxSelect(lessonsRemaining / (program.program.LessonsPerSession || 1))
    }
    document.body.style.cssText = 'cursor:auto'
    setLessonCount(lessons.length * (program.program?.LessonsPerSession || 1))
  }

  const setAsFailed = (isExcess?: boolean) => {
    setters.failed([...failed, current] as DateRanges)
    const newLesson = { ...lessons.find((lesson) => lesson.Status === 'loading'), isExcess } as BookedLesson
    newLesson.Status = 'error'
    setters.lessons([
      ...lessons.map((lesson) => ({ ...lesson, disabled: false })).filter((lesson) => lesson.Status !== 'loading'),
      newLesson,
    ])
    state.allowed && setters.selectLesson({ ...newLesson, Status: 'error' }, 'error')
    setters.current(undefined)
  }

  const hasExceededHours = (time: StringRange) => {
    const start = moment.tz(time[0], 'GMT')
    const end = moment.tz(time[1], 'GMT')
    const dailyHours = instructorHours?.instructorHoursInfo?.DailyHoursInfo || []
    const matchDH = dailyHours.find((item) => item.StartDateTime === start.clone().startOf('day').toISOString())
    const remaining = matchDH?.DailyHoursRemaining || 0
    const newHrs = end.diff(start, 'h', true)
    return matchDH && (remaining === 0 || remaining - newHrs < 0)
  }

  const book = async (action?: string, savePref?: boolean) => {
    const RegUserObj =
      isRegionalUser && UserId
        ? {
            UserId,
          }
        : {}
    // @ts-ignore
    window.hj('event', 'Instructor Calendar Booking')
    const sessionIdQuery = router.query.sessionId
    setLoadingModal(true)

    const actionPayload = SessionPrefFlag
      ? { InstructorUnavailabilityAction: action || program.program?.InstructorUnavailabilityAction || '' }
      : {}

    const getLessonPayload = (lesson: BookedLesson, InstructorId?: string) => ({
      StudentId: profile.SFId,
      InstructorId: InstructorId || lesson.InstructorId,
      FromDatetime: lesson.StartTime,
      ToDatetime: lesson.EndTime,
      SessionId: lesson.Id,
      ProgramId: lesson.ProgramId,
      Timezone: profile.Timezone,
      Origin: isForBulkUsersOnly || isBulkPage ? PORTAL_ORIGIN.FMI_BULK : PORTAL_ORIGIN.FMI,
      ...RegUserObj,
      ...actionPayload,
    })

    const Sessions = state.allowed
      ? flatten(
          Object.keys(instructorLessons || {})?.map((InstructorId) =>
            instructorLessons?.[InstructorId]?.lessons?.map((lesson) => getLessonPayload(lesson, InstructorId))
          )
        )
      : lessons.map((lesson) => getLessonPayload(lesson))

    let variables: any = { Sessions }

    if (sessionIdQuery) {
      variables = {
        Session: {
          ExemptFromAllowance: false,
          ExemptionReason: 'Other',
          NewSessionId: lessons[0].Id,
          PreviousSessionId: sessionIdQuery,
          Origin: PORTAL_ORIGIN.FMI_RESCHEDULE,
          ...RegUserObj,
          ...actionPayload,
        },
      }
    }

    let response: FetchResult<any> = {}

    if (savePref) {
      const bookingMutation = client.mutate({
        mutation: sessionIdQuery ? CONFIRM_RESCHEDULE : BOOK_SESSIONS,
        variables,
      })

      const programMutation = client.mutate({
        mutation: PUT_PROGRAMS,
        variables: {
          programInput: {
            ProgramStudentInfo: {
              ProgramId: program.ProgramId,
              InstructorUnavailabilityAction: action,
            },
          },
        },
      })

      await Promise.all([bookingMutation, programMutation])
        .then((mutations) => {
          response = mutations[0]

          if (!mutations[1].data?.putPrograms?.Success) {
            console.error('Unable to update program preference')
          }
        })
        .catch((e) => console.error(e))
    } else {
      response = await client.mutate({
        mutation: sessionIdQuery ? CONFIRM_RESCHEDULE : BOOK_SESSIONS,
        variables,
      })
    }

    setLoadingModal(true)

    // TODO: update logic to determine which session failed to be booked
    const schedules = lessons.map((item) => {
      return (
        moment
          .tz(item.StartTime, profile.Timezone)
          .format(`DD MMM YYYY, ${isTimeFormatDisabled ? 'h:mm' : DATE_FORMAT.hmmA}`) +
        ' - ' +
        moment.tz(item.EndTime, profile.Timezone).format(DATE_FORMAT.hmmA)
      )
    })

    const Status: string =
      response?.data?.[sessionIdQuery ? 'confirmRescheduleSession' : 'bookSessions']?.ServiceResult?.Status || ''

    if (Status.toLowerCase() === 'success') {
      setConfirmedLessons(schedules)
    } else {
      setUnavailableLessons(schedules)
      deleteAll()
    }

    const [updateUI] = await refetchProfile()
    await updateUI()
    // add delay so new session gets posted
    setTimeout(() => {
      setters.lessons([])
      setters.failed([])
      setters.loading([])
      setters.selected([])
      setters.notSaved(false)
      setLessonCount(0)
      setExpand(false)
      refresh().then(() => {
        showBookingInfoModal(true)
        setLoadingModal(false)
      })
    }, 1000)
  }

  const convertBookInfoModalTimeFormat = (lessons: string[]) =>
    isTimeFormatDisabled
      ? lessons
      : lessons?.map((dt) => {
          const splits = dt.split(', ')
          return `${splits[0]}, ${convertToUserTimeFormats(
            splits[1].split(' - '),
            [DATE_FORMAT.hmmA, DATE_FORMAT.hmmA],
            [DATE_FORMAT.hmm, DATE_FORMAT.hmmA]
          ).join(' - ')}`
        })

  useEffect(() => {
    if (deleteAllSilently) {
      deleteAllRecord({
        variables: {
          ids: lessons
            .map((lesson) => {
              lesson.disabled = true
              return lesson.Id
            })
            .filter(Boolean)
            .join(','),
        },
      })
    }
  }, [deleteAllSilently])

  useEffect(() => {
    if (deleteAllData || deletingAllTempSession) {
      lessons.map(
        (lesson) =>
          !lesson.isExcess &&
          updateDHRemaining(
            godMode,
            instructorHours,
            moment.tz(lesson.StartTime, 'GMT'),
            moment.tz(lesson.EndTime, 'GMT'),
            true,
            setters.instructorHours
          )
      )
      setters.disabled(false)
      setters.lessons([])
      setters.failed([])
      setters.loading([])
      setters.selected([])
      setters.deleteAllSilently(false)
      setLessonCount(0)
      setExpand(false)
    }

    setters.deleteStorage(deletingAllTempSession)
  }, [deleteAllData, deletingAllTempSession])

  useEffect(() => {
    // TODO: add error handler
    if (deleteAllError) {
      setters.disabled(false)
      setters.loading([])
      setOpenUnknownError(true)
    }
  }, [deleteAllError])

  useEffect(() => {
    if (validationFailed) {
      setAsFailed(true) // isExcess true to exclude from daily hours calculation
      turnOffLoading()
      setValidationFailed(false)
    }
  }, [validationFailed])

  useEffect(() => {
    if (current) {
      setters.maxSelect(0)
      document.body.style.cssText = 'cursor:wait !important'

      let SessionInfo: {
        StudentId: string
        InstructorId: string
        ProgramId: string
        StartTime: string
        EndTime: string
        RescheduledSessionId?: string
      } = {
        StudentId: profile.SFId,
        InstructorId: instructor,
        ProgramId: program.ProgramId || '',
        StartTime: current[0],
        EndTime: current[1],
      }

      if (sessionIdQuery) {
        SessionInfo = {
          ...SessionInfo,
          RescheduledSessionId: sessionIdQuery as string,
        }
      }

      setters.lessons([
        ...lessons.map((lesson) => ({ ...lesson, disabled: true })),
        {
          ...SessionInfo,
          Status: 'loading',
        },
      ])
      state.allowed &&
        setters.selectLesson(
          {
            ...SessionInfo,
            Status: 'loading',
          },
          'loading'
        )

      const start = moment.tz(current[0], 'GMT')
      const end = moment.tz(current[1], 'GMT')

      if (
        ((start.isSame(end, 'day') || start.clone().add(1, 'day').startOf('day').isSame(end)) &&
          hasExceededHours(current)) ||
        (!start.isSame(end, 'day') &&
          (hasExceededHours([start.toISOString(), start.clone().add(1, 'day').startOf('day').toISOString()]) ||
            hasExceededHours([end.clone().startOf('day').toISOString(), end.toISOString()])))
      ) {
        setValidationFailed(true)
        return
      }
      updateDHRemaining(godMode, instructorHours, start, end, false, setters.instructorHours)
      putTempHold({
        variables: {
          createTempHoldInput: { SessionInfo },
        },
      })
    }
  }, [JSON.stringify(current)])

  useEffect(() => {
    if (!tempHoldLoading) {
      turnOffLoading()
    }
  }, [tempHoldLoading])

  useEffect(() => {
    if (tempHoldError && current) {
      setAsFailed()
    }
  }, [tempHoldError])

  useEffect(() => {
    if (tempHoldData) {
      const newLesson = tempHoldData.createTempHold.ServiceResult[0].SessionInfo as BookedLesson
      newLesson.Status = 'created'
      setters.lessons([
        ...lessons.map((lesson) => ({ ...lesson, disabled: false })).filter((lesson) => lesson.Status !== 'loading'),
        newLesson,
      ])
      state.allowed && setters.selectLesson({ ...newLesson, Status: 'created' }, 'created')
      setters.current(undefined)
    }
  }, [tempHoldData])

  useEffect(() => {
    if (deselected) {
      const session = lessons.find((lesson) => lesson.StartTime === deselected[0]) as BookedLesson

      if (session?.Id) {
        setters.loading([deselected])
        deleteRecord({
          variables: {
            ids: session.Id,
          },
        })
        session.Status = 'loading'
        const newLessons = [
          ...lessons.filter((lesson) => {
            lesson.disabled = true
            return lesson.Id !== session.Id
          }),
          session,
        ]
        setters.lessons(newLessons)
        state.allowed && setters.deselectLesson({ ...session, Status: 'loading' }, 'loading')
      } else {
        const newLesson = [...lessons.filter((lesson) => lesson.StartTime !== deselected[0])]
        setters.lessons(newLesson)
        state.allowed &&
          setters.deselectLesson(
            lessons?.find((lesson) => lesson.StartTime === deselected[0]),
            'deleted'
          )
        setters.selected([...selected.filter((item) => item[0] !== deselected[0])])
        setLessonCount(newLesson.length * (program.program?.LessonsPerSession || 1))
      }

      setters.failed([...failed.filter((item) => item[0] !== session.StartTime)])

      if (!state.allowed || (state.allowed && session?.InstructorId === instructor)) {
        !session?.isExcess &&
          updateDHRemaining(
            godMode,
            instructorHours,
            moment.tz(deselected[0], 'GMT'),
            moment.tz(deselected[1], 'GMT'),
            true,
            setters.instructorHours
          )
      }
    }
  }, [deselected])

  useEffect(() => {
    if (deleteData && deselected) {
      const newLessons = [
        ...lessons.filter((lesson) => {
          lesson.disabled = false
          return lesson.StartTime !== deselected[0]
        }),
      ]

      setters.loading([])
      setters.lessons(newLessons)
      state.allowed &&
        setters.deselectLesson(
          lessons.find((lesson) => lesson.StartTime === deselected[0]),
          'deleted'
        )
      setLessonCount(newLessons.length * (program.program?.LessonsPerSession || 1))
      setExpand(expand && newLessons.length > 0)
      setters.selected([...selected.filter((item) => item[0] !== deselected[0])])
    }
  }, [deleteData])

  useEffect(() => {
    if (deleteError) {
      setters.loading([])
      // TODO: invoke error modal
      setOpenUnknownError(true)
    }
  }, [deleteError])

  useEffect(() => {
    setLessonsRemaining(
      sessionIdQuery ? program.program?.LessonsPerSession || 1 : program.program?.LessonsRemaining || 0
    )
  }, [program.program?.LessonsRemaining])

  const TimerComponent = useMemo(
    () =>
      lessons.length > 0 ? (
        <TimeWrapper>
          <Timer
            label={intl.formatMessage({ id: 'minutes remaining' })}
            type="warning"
            fullWidth
            onStop={() => {
              setSessionExpiredModal(true)
              deleteAll()
            }}
          />
        </TimeWrapper>
      ) : null,
    [lessons]
  )

  const ReadOnlyMsg = useMemo(() => {
    if (!program.isStudentAbleToBook) {
      return (
        <FormattedMessage
          id={`You are in read-only mode. Your lessons are scheduled by ${
            program?.program?.IsPrivate ? `the lead student.` : `your learning centre.`
          }`}
          defaultMessage={`You are in read-only mode. Your lessons are scheduled by ${
            program?.program?.IsPrivate ? `the lead student.` : `your learning centre.`
          }`}
        />
      )
    }

    if (program.IsOrientationRequired) {
      return (
        <FormattedMessage
          id="You are in read-only mode. Before we book your lessons let's organise your Getting Started or Orientation Session. Once this has been booked you can come back and book your lessons."
          defaultMessage="You are in read-only mode. Before we book your lessons let's organise your Getting Started or Orientation Session. Once this has been booked you can come back and book your lessons."
        />
      )
    }

    if (program.IsLessonsBookedOut) {
      return (
        <FormattedMessage
          id="You are in read-only mode. All your lessons have been booked."
          defaultMessage="You are in read-only mode. All your lessons have been booked."
        />
      )
    }

    return null
  }, [readOnly, program.isStudentAbleToBook, program.IsOrientationRequired, program.IsLessonsBookedOut])

  const ExcessLessonMsg = useMemo(() => {
    if (!sessionIdQuery && program.hasExcessUnbookedLessons(selected, !isEmpty(sessionIdQuery))) {
      return (
        <AlertWrapper>
          <MaxAlert type="warning">
            <FormattedMessage
              id="You have {lessonsRemaining, plural,=0{0 lesson}one{1 lesson}other{# lessons}} remaining to be booked. However a minimum of {lessonsPerSession, plural,=0{0 lesson}one{1 lesson}other{# lessons}} is required when booking. Please contact support for help with your booking."
              defaultMessage="You have {lessonsRemaining, plural,=0{0 lesson}one{1 lesson}other{# lessons}} remaining to be booked. However a minimum of {lessonsPerSession, plural,=0{0 lesson}one{1 lesson}other{# lessons}} is required when booking. Please contact support for help with your booking."
              values={{
                lessonsRemaining: program.getExcessLessonsCount(selected),
                lessonsPerSession: program.program?.LessonsPerSession || 0,
              }}
            />
          </MaxAlert>
        </AlertWrapper>
      )
    }

    return null
  }, [selected, lessons])

  const getLessonCardTime = (start: moment.Moment, end: moment.Moment, withDate: boolean = false) => {
    if (!isTimeFormatDisabled) {
      const time =
        convertToUserTimeFormats(
          [start.format(DATE_FORMAT.HMa), end.format(DATE_FORMAT.HMa)],
          [DATE_FORMAT.HMa, DATE_FORMAT.HMa]
        )?.join(' - ') || ''

      return withDate ? `${start.format('DD MMM')}, ${time}` : time
    }

    return `${start.format('DD MMM, hh:mm')} - ${end.format('hh:mm a')}`
  }

  const BulkBookedLessonCards = useMemo(() => {
    if (isForBulkUsersOnly && instructorLessons) {
      const bulkSidebarLessons = getBulkSidebarLessons(lessons, profile)
      const monthsAndYears = orderBy(Object.keys(bulkSidebarLessons), [(date) => new Date(date)], ['asc'])

      return monthsAndYears.map((date, index) => {
        const bulkLessons = bulkSidebarLessons[date] || []

        return (
          <InstructorSessions key={`${date}-${index}`} style={{ marginBottom: '12px' }}>
            <InstructorName isActive>{moment(date).format(DATE_FORMAT.MY)}</InstructorName>

            {bulkLessons
              .sort((a, b) => new Date(a.StartTime).valueOf() - new Date(b.StartTime).valueOf())
              .map((lesson, i) => {
                const selectedInstructor = instructorLessons?.[lesson?.InstructorId]
                const instructorName = selectedInstructor?.name || ''
                const start = moment.tz(lesson.StartTime, profile.Timezone)
                const end = moment.tz(lesson.EndTime, profile.Timezone)

                return (
                  <React.Fragment key={i}>
                    <BookedLessonCard
                      name={instructorName}
                      date={''}
                      time={getLessonCardTime(start, end, true)}
                      loading={lesson.Status === 'loading'}
                      error={lesson.Status === 'error'}
                      disabled={lesson.disabled}
                      smallXButton
                      onClose={() => {
                        setters.deselected([lesson.StartTime, lesson.EndTime])
                      }}
                      errorTitle={intl.formatMessage({
                        id: 'Lesson not available',
                        defaultMessage: 'Lesson not available',
                      })}
                      errorMessage={intl.formatMessage({
                        id: 'Please remove and try another time',
                        defaultMessage: 'Please remove and try another time',
                      })}
                    />

                    <Spacer size="xxxs" />
                  </React.Fragment>
                )
              })}
          </InstructorSessions>
        )
      })
    }

    return null
  }, [lessons, profile, instructorLessons])

  const LessonSelectedMessage = useMemo(() => {
    return (
      <LessonSelected isGroupLead={program.isGroupLead} isMobile>
        <div className="count-label">
          <LessonSelectedCount>{lessonCount + '/' + lessonsRemaining}&nbsp;</LessonSelectedCount>
          <FormattedMessage id="lessons selected" defaultMessage="lessons selected" />
        </div>
        {program.isGroupLead && (
          <div>
            <Tooltip
              message={intl.formatMessage({
                id: "Don't forget. You are the lead student for your group. You are booking lessons for all attendees.",
                defaultMessage:
                  "Don't forget. You are the lead student for your group. You are booking lessons for all attendees.",
              })}
              placement="topRight"
              visible={showGroupToolTip}
            >
              <Button onClick={() => setShowGroupToolTip(!showGroupToolTip)}>
                <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="m6.028 1.288 5.779 2.34a.31.31 0 0 1-.008.576L5.98 6.372a.516.516 0 0 1-.362-.001L1.75 4.803l.002 4.137c.313.2.521.55.521.95v1.86H.022V9.89c0-.482.302-.892.727-1.053V4.398l-.508-.205a.35.35 0 0 1-.003-.648L5.636 1.29a.516.516 0 0 1 .392-.002zm3.28 4.504v2.12c-.93.842-2.072 1.263-3.427 1.263s-2.524-.42-3.506-1.262V5.792l3.444 1.35 3.488-1.35z"
                    fill="#FFF"
                    fillRule="evenodd"
                  />
                </svg>
              </Button>
            </Tooltip>
          </div>
        )}
      </LessonSelected>
    )
  }, [program.isGroupLead, lessonCount, lessonsRemaining, showGroupToolTip])

  const BookButtonLabel = useMemo(() => {
    return (
      <>
        {isForBulkUsersOnly && !sessionIdQuery ? (
          <>
            {!!failed.length || disabled || !lessonCount || disableBookButton ? (
              <BulkBookButton>
                <Warning className="icon" />
                <FormattedMessage id="Select all lessons to book" defaultMessage="Select all lessons to book" />
              </BulkBookButton>
            ) : (
              <FormattedMessage
                id="follow-instructor-book-lesson"
                defaultMessage="{value,plural,=0{Book}one{Book # lesson}other{Book # lessons}}"
                values={{ value: lessonCount }}
              />
            )}
          </>
        ) : sessionIdQuery ? (
          <FormattedMessage
            id="follow-instructor-rebook-lesson"
            defaultMessage="{value,plural,=0{Re-book}one{Re-book 1 lesson}other{Re-book # lessons}}"
            values={{ value: program.program?.LessonsPerSession || 0 }}
          />
        ) : (
          <FormattedMessage
            id="follow-instructor-book-lesson"
            defaultMessage="{value,plural,=0{Book}one{Book # lesson}other{Book # lessons}}"
            values={{ value: lessonCount }}
          />
        )}
      </>
    )
  }, [
    isForBulkUsersOnly,
    sessionIdQuery,
    failed,
    disabled,
    lessonCount,
    disableBookButton,
    program.program?.LessonsPerSession,
  ])

  return (
    <>
      <DWrapper>
        <DContent>
          {TimerComponent}
          {lessons.length > 0 && <Spacer size="lg" />}

          <Header>
            {loading ? (
              <Skeleton height="18px" width="70px" />
            ) : (
              <>
                <Title>
                  <span className="title">
                    <FormattedMessage id="Lessons" defaultMessage="Lessons" />
                  </span>
                  <Tooltip
                    message={intl.formatMessage({
                      id: 'Lessons are held for approx 20 mins.',
                    })}
                    placement="topRight"
                  >
                    <InfoWrapper>
                      <InfoCircle size="sm" />
                    </InfoWrapper>
                  </Tooltip>
                </Title>
                {!!lessonCount && (
                  <StyledBtn outline disabled={disabled} onClick={() => setOpenWarningModal(true)}>
                    <FormattedMessage id="Delete all" defaultMessage="Delete all" />
                  </StyledBtn>
                )}
              </>
            )}
          </Header>
          <LessonWrapper>
            {!state.allowed && !isForBulkUsersOnly
              ? lessons
                  .sort((a, b) => new Date(a.StartTime).valueOf() - new Date(b.StartTime).valueOf())
                  .map((lesson, i) => {
                    const start = moment.tz(lesson.StartTime, profile.Timezone)
                    const end = moment.tz(lesson.EndTime, profile.Timezone)
                    return (
                      <BookedLessonCard
                        key={i}
                        date={start.format('DD MMMM YYYY')}
                        time={getLessonCardTime(start, end)}
                        loading={lesson.Status === 'loading'}
                        error={lesson.Status === 'error'}
                        disabled={lesson.disabled}
                        onClose={() => {
                          setters.deselected([lesson.StartTime, lesson.EndTime])
                        }}
                        errorTitle={intl.formatMessage({
                          id: 'Lesson not available',
                          defaultMessage: 'Lesson not available',
                        })}
                        errorMessage={intl.formatMessage({
                          id: 'Please remove and try another time',
                          defaultMessage: 'Please remove and try another time',
                        })}
                      />
                    )
                  })
              : null}
            {BulkBookedLessonCards}

            {state.allowed &&
              !isForBulkUsersOnly &&
              instructorLessons &&
              Object.keys(instructorLessons || {})
                ?.reverse()
                ?.map((instructorId, index) => {
                  const selectedInstructor = instructorLessons?.[instructorId]
                  const instructorName = selectedInstructor?.name || ''
                  const lessons = selectedInstructor?.lessons || []
                  const isActive = instructorId === instructor

                  if (!isActive && !lessons.length) {
                    return <></>
                  }

                  return (
                    <InstructorSessions key={`instructor-sessions-${index}`}>
                      <InstructorName isActive={isActive}>{instructorName}</InstructorName>

                      {lessons.length ? (
                        lessons
                          .sort((a, b) => new Date(a.StartTime).valueOf() - new Date(b.StartTime).valueOf())
                          .map((lesson, i) => {
                            const start = moment.tz(lesson.StartTime, profile.Timezone)
                            const end = moment.tz(lesson.EndTime, profile.Timezone)
                            return (
                              <BookedLessonCardWrapper key={`booked-lesson-card-wrapper-${i}`}>
                                <BookedLessonCard
                                  date={start.format('DD MMMM YYYY')}
                                  time={getLessonCardTime(start, end)}
                                  loading={lesson.Status === 'loading'}
                                  error={lesson.Status === 'error'}
                                  disabled={loading || deletingAll || tempHoldLoading || lesson.disabled}
                                  onClose={() => {
                                    setters.deselected([lesson.StartTime, lesson.EndTime])
                                  }}
                                  errorTitle={intl.formatMessage({
                                    id: 'Lesson not available',
                                    defaultMessage: 'Lesson not available',
                                  })}
                                  errorMessage={intl.formatMessage({
                                    id: 'Please remove and try another time',
                                    defaultMessage: 'Please remove and try another time',
                                  })}
                                />
                              </BookedLessonCardWrapper>
                            )
                          })
                      ) : (
                        <NoLessonsSelected>
                          <div className="icon">
                            <Calendar1 />
                          </div>
                          <FormattedMessage id="No lessons selected" defaultMessage="No lessons selected" />
                        </NoLessonsSelected>
                      )}
                    </InstructorSessions>
                  )
                })}
          </LessonWrapper>
          <Footer>
            {loading ? (
              <Skeleton width="235px" height="69px" />
            ) : (
              <>
                {readOnly ? (
                  <AlertWrapper>
                    <MaxAlert type="warning">{ReadOnlyMsg}</MaxAlert>
                  </AlertWrapper>
                ) : (
                  <>
                    <Spacer size="xs" />
                    {ExcessLessonMsg}
                    {LessonSelectedMessage}
                  </>
                )}
                <Submit
                  onClick={() => {
                    if (!program.program?.InstructorUnavailabilityAction && SessionPrefFlag) {
                      setOpenPrefModal(true)
                    } else {
                      book()
                    }
                  }}
                  disabled={!!failed.length || disabled || !lessonCount || disableBookButton}
                  isBulkBook={isForBulkUsersOnly}
                >
                  {BookButtonLabel}
                </Submit>
              </>
            )}
          </Footer>
        </DContent>
      </DWrapper>
      {!loading && (
        <MWrapper>
          <ShadowExpand />
          <ExpandBtn onClick={() => lessonCount && setExpand(!expand)}>
            {expand ? <ArrowDown /> : <ArrowUp />}
          </ExpandBtn>
          {expand && (
            <div>
              <Header>
                <Title>
                  <span>
                    <FormattedMessage id="Lessons" defaultMessage="Lessons" />
                  </span>
                  <Tooltip
                    message={intl.formatMessage({
                      id: 'Lessons are held for approx 20 mins.',
                    })}
                    placement="top"
                  >
                    <InfoWrapper>
                      <InfoCircle size="sm" />
                    </InfoWrapper>
                  </Tooltip>
                </Title>
                {!!lessonCount && (
                  <StyledBtn outline disabled={disabled} onClick={() => setOpenWarningModal(true)}>
                    <FormattedMessage id="Delete all" defaultMessage="Delete all" />
                  </StyledBtn>
                )}
              </Header>
              <LessonWrapper mobile>
                {!state.allowed && !isForBulkUsersOnly
                  ? lessons
                      .sort((a, b) => new Date(a.StartTime).valueOf() - new Date(b.StartTime).valueOf())
                      .map((lesson, i) => {
                        const start = moment.tz(lesson.StartTime, profile.Timezone)
                        const end = moment.tz(lesson.EndTime, profile.Timezone)
                        return (
                          <BookedLessonCard
                            key={i}
                            date={start.format('DD MMMM YYYY')}
                            time={getLessonCardTime(start, end)}
                            loading={lesson.Status === 'loading'}
                            error={lesson.Status === 'error'}
                            disabled={lesson.disabled}
                            onClose={() => {
                              setters.deselected([lesson.StartTime, lesson.EndTime])
                            }}
                            errorTitle={intl.formatMessage({
                              id: 'Lesson not available',
                              defaultMessage: 'Lesson not available',
                            })}
                            errorMessage={intl.formatMessage({
                              id: 'Please remove and try another time',
                              defaultMessage: 'Please remove and try another time',
                            })}
                          />
                        )
                      })
                  : null}
                {BulkBookedLessonCards}

                {state.allowed &&
                  !isForBulkUsersOnly &&
                  instructorLessons &&
                  Object.keys(instructorLessons || {})
                    ?.reverse()
                    ?.map((instructorId, index) => {
                      const selectedInstructor = instructorLessons?.[instructorId]
                      const instructorName = selectedInstructor?.name || ''
                      const lessons = selectedInstructor?.lessons || []
                      const isActive = instructorId === instructor

                      if (!isActive && !lessons.length) {
                        return <></>
                      }

                      return (
                        <InstructorSessions key={`bulk-instructor-sessions-${index}`}>
                          <InstructorName isActive={isActive}>{instructorName}</InstructorName>

                          {lessons.length ? (
                            lessons
                              .sort((a, b) => new Date(a.StartTime).valueOf() - new Date(b.StartTime).valueOf())
                              .map((lesson, i) => {
                                const start = moment.tz(lesson.StartTime, profile.Timezone)
                                const end = moment.tz(lesson.EndTime, profile.Timezone)
                                return (
                                  <BookedLessonCardWrapper key={`bulk-booked-lesson-card-wrapper-${i}`}>
                                    <BookedLessonCard
                                      date={start.format('DD MMMM YYYY')}
                                      time={getLessonCardTime(start, end)}
                                      loading={lesson.Status === 'loading'}
                                      error={lesson.Status === 'error'}
                                      disabled={deletingAll || tempHoldLoading || lesson.disabled}
                                      onClose={() => {
                                        setters.deselected([lesson.StartTime, lesson.EndTime])
                                      }}
                                      errorTitle={intl.formatMessage({
                                        id: 'Lesson not available',
                                        defaultMessage: 'Lesson not available',
                                      })}
                                      errorMessage={intl.formatMessage({
                                        id: 'Please remove and try another time',
                                        defaultMessage: 'Please remove and try another time',
                                      })}
                                    />
                                  </BookedLessonCardWrapper>
                                )
                              })
                          ) : (
                            <NoLessonsSelected>
                              <div className="icon">
                                <Calendar1 />
                              </div>
                              <FormattedMessage id="No lessons selected" defaultMessage="No lessons selected" />
                            </NoLessonsSelected>
                          )}
                        </InstructorSessions>
                      )
                    })}
              </LessonWrapper>
            </div>
          )}
          <div>
            {TimerComponent}
            {lessons.length > 0 && <Spacer size="xs" />}

            {readOnly ? (
              <AlertWrapper>
                <MaxAlert type="warning">{ReadOnlyMsg}</MaxAlert>
              </AlertWrapper>
            ) : (
              <>
                {ExcessLessonMsg}
                {LessonSelectedMessage}
              </>
            )}
            <Submit
              onClick={() => {
                if (!program.program?.InstructorUnavailabilityAction && SessionPrefFlag) {
                  setOpenPrefModal(true)
                } else {
                  book()
                }
              }}
              mobile
              disabled={!!failed.length || disabled || !lessonCount || disableBookButton}
              isBulkBook={isForBulkUsersOnly}
            >
              {BookButtonLabel}
            </Submit>
          </div>
        </MWrapper>
      )}
      <WarningModal open={openUnknownError} onClose={() => setOpenUnknownError(false)} anApiError noButtons />
      <WarningModal
        open={openWarningModal}
        onClose={() => {
          setOpenWarningModal(false)
        }}
        onCancel={() => {
          setOpenWarningModal(false)
        }}
        title={intl.formatMessage({ id: 'Delete all lessons', defaultMessage: 'Delete all lessons' })}
        description={
          <>
            <div>
              {intl.formatMessage({
                id: 'Are you sure you want to delete all lessons?',
                defaultMessage: 'Are you sure you want to delete all lessons?',
              })}
            </div>
            <div>
              {intl.formatMessage({
                id: 'This action cannot be undone.',
                defaultMessage: 'This action cannot be undone.',
              })}
            </div>
          </>
        }
        proceedBtnType="error"
        cancelLabel={intl.formatMessage({ id: 'Cancel', defaultMessage: 'Cancel' })}
        proceedLabel={intl.formatMessage({ id: 'Delete', defaultMessage: 'Delete' })}
        onProceed={() => deleteAll()}
      />

      <LoadingModal
        open={loadingModal}
        onClose={() => {}}
        title={`${intl.formatMessage({
          id: 'Checking booking details...',
        })}`}
        subtitle={`${intl.formatMessage({ id: 'Please wait a moment' })}.`}
      />
      <BookingInformationModal
        open={bookingInfoModal}
        title={
          unavailableLessons.length
            ? intl.formatMessage({ id: 'Booking information' })
            : intl.formatMessage({ id: 'Booking confirmed' })
        }
        description={
          unavailableLessons.length ? (
            <div>
              <div>{intl.formatMessage({ id: 'See your booking summary below.' })}</div>
              <div>
                {intl.formatMessage({
                  id: 'Some of your selections timed out.',
                })}
              </div>
              <div>
                {intl.formatMessage({
                  id: 'Try booking the same times and dates again.',
                })}
              </div>
            </div>
          ) : (
            <div>
              <div>{intl.formatMessage({ id: `We'll see you soon!` })}</div>
            </div>
          )
        }
        closeText={intl.formatMessage({ id: 'Close' })}
        unavailableLessonText={intl.formatMessage({ id: 'Unavailable lessons' })}
        bookedLessonText={intl.formatMessage({ id: 'Booked lessons' })}
        onClose={() => {
          showBookingInfoModal(false)
          if (!unavailableLessons.length) {
            router.push(
              isRegionalUser
                ? `${baseRoute}?${getRouteParams(['instructorId', 'programId', 'sessionId'])}`
                : '/scheduling/my-calendar'
            )
          }
          setConfirmedLessons([])
          setUnavailableLessons([])
        }}
        bookedLessons={convertBookInfoModalTimeFormat(confirmedLessons)}
        unavailableLessons={convertBookInfoModalTimeFormat(unavailableLessons)}
      />

      <Modal open={sessionExpiredModal} onClose={() => setSessionExpiredModal(false)}>
        <div>
          <TransactionalModal
            closeBtnLabel={intl.formatMessage({
              id: 'Find an instructor',
              defaultMessage: 'Find an instructor',
            })}
            description={intl.formatMessage({
              id: "We're sorry, unfortunately your lesson selections have expired. Search again for your selected lessons, or return to the instructor page to browse instructors.",
              defaultMessage:
                "We're sorry, unfortunately your lesson selections have expired. Search again for your selected lessons, or return to the instructor page to browse instructors.",
            })}
            image={<ImageCalendarEventUnavailable />}
            onClose={() =>
              router.push(
                isRegionalUser
                  ? `${baseRoute}?${getRouteParams(['instructorId', 'programId', 'sessionId'])}`
                  : '/scheduling/instructors'
              )
            }
            onProceed={() => setSessionExpiredModal(false)}
            proceedBtnLabel={intl.formatMessage({
              id: 'Search again',
              defaultMessage: 'Search again',
            })}
            title={intl.formatMessage({
              id: 'Your session has expired.',
              defaultMessage: 'Your session has expired.',
            })}
          />
        </div>
      </Modal>
      <SessionPreferenceModal
        open={openPrefModal}
        onClose={() => setOpenPrefModal(false)}
        onSubmit={(action, savePref) => {
          setOpenPrefModal(false)
          book(action, savePref)
        }}
      />
    </>
  )
}

export default Sidebar
